import type { UnitStatReportExposureViewModel } from 'src/api/types/UnitStatReportExposureViewModel';
import type {
  ClassCodeLookupOption,
  CodeDescriptionLookupOption,
  UpdatePolicyUnitStatReportExposureRequest,
} from 'src/api/services/policy.service';

import { formatDate } from 'src/sections/employer-data/shared/utils/policyUtils';

export type UnitStatExposureDraftRow = {
  clientId: string;
  usrexposureId?: number;
  modType?: string;
  updateTypeDescription?: string;
  classDescription?: string;
  classCode: string;
  expModFactor: string;
  expModFactorEffectiveDate: string;
  rateEffectiveDate: string;
  exposure: string;
  manualRate: string;
  premium: string;
  splitPeriodCode: string;
  updateTypeCode: string;
  isNew: boolean;
};

export type UnitStatExposureDraftDelta = {
  rows: UnitStatExposureDraftRow[];
  deletedExposureIds: number[];
};

export const createUnitStatExposureDraft = (
  exposures: UnitStatReportExposureViewModel[]
): UnitStatExposureDraftDelta => ({
  rows: exposures.map((exposure) => ({
    clientId: `existing-${exposure.usrexposureId}`,
    usrexposureId: exposure.usrexposureId,
    modType: exposure.class?.classStatCategory?.statCategoryDescription ?? '',
    updateTypeDescription: exposure.updateType?.updateTypeDescription ?? '',
    classDescription: exposure.class?.description ?? '',
    classCode: exposure.class?.classCode ?? '',
    expModFactor: exposure.expModFactor?.toString() ?? '',
    expModFactorEffectiveDate: formatDate(exposure.expModFactorEffectiveDate),
    rateEffectiveDate: formatDate(exposure.rateEffectiveDate),
    exposure: exposure.exposure?.toString() ?? '',
    manualRate: exposure.manualRate?.toString() ?? '',
    premium: exposure.premium?.toString() ?? '',
    splitPeriodCode: exposure.splitPeriod?.splitPeriodCode ?? '',
    updateTypeCode: exposure.updateType?.updateTypeCode ?? '',
    isNew: false,
  })),
  deletedExposureIds: [],
});

export const applyUnitStatExposureDraftDelta = (
  snapshot: UnitStatExposureDraftDelta,
  delta: UnitStatExposureDraftDelta | null | undefined
): UnitStatExposureDraftDelta => delta ?? snapshot;

export const getUnitStatExposureDraftDelta = (
  snapshot: UnitStatExposureDraftDelta,
  draft: UnitStatExposureDraftDelta
): UnitStatExposureDraftDelta | null =>
  JSON.stringify(snapshot) === JSON.stringify(draft) ? null : draft;

const toOptionalNumber = (value: string) => (value === '' ? undefined : Number(value));

export const createUnitStatExposureRequests = (
  draft: UnitStatExposureDraftDelta
): UpdatePolicyUnitStatReportExposureRequest[] =>
  draft.rows.filter((row) => row.isNew).map((row) => ({
    ...(row.usrexposureId ? { usrexposureId: row.usrexposureId } : {}),
    classCode: row.classCode,
    expModFactor: toOptionalNumber(row.expModFactor),
    expModFactorEffectiveDate: row.expModFactorEffectiveDate || undefined,
    rateEffectiveDate: row.rateEffectiveDate || undefined,
    exposure: toOptionalNumber(row.exposure),
    manualRate: toOptionalNumber(row.manualRate),
    premium: toOptionalNumber(row.premium),
    splitPeriodCode: row.splitPeriodCode || undefined,
    updateTypeCode: row.updateTypeCode || undefined,
  }));

export const validateUnitStatExposureDraft = (
  draft: UnitStatExposureDraftDelta,
  classCodes: ClassCodeLookupOption[]
): Record<string, string> => {
  const errors: Record<string, string> = {};
  draft.rows.filter((row) => row.isNew).forEach((row) => {
    const prefix = row.clientId;
    const selectedClassCode = classCodes.find((option) => option.classCode === row.classCode);
    if (!/^\d{4}$/.test(row.classCode) || !selectedClassCode) {
      errors[`${prefix}.classCode`] = 'Enter a valid four-digit class code.';
    }
    if (!row.updateTypeCode) errors[`${prefix}.updateTypeCode`] = 'Select an update type.';
    // Experience mod is required for every new exposure regardless of the class code's
    // subjectToExperienceMod flag; only Exposure is gated by that flag.
    if (!/^\d+\.\d{1,2}$/.test(row.expModFactor) || Number(row.expModFactor) === 0) {
      errors[`${prefix}.expModFactor`] = 'Experience mod is required, must include a decimal with up to two decimal places (e.g., 1.23), and cannot be zero.';
    }
    if (selectedClassCode?.subjectToExperienceMod) {
      if (!/^\d+$/.test(row.exposure) || row.exposure === '') {
        errors[`${prefix}.exposure`] = 'Exposure is required and must be a whole dollar amount with no decimals.';
      }
    } else if (row.exposure !== '') {
      errors[`${prefix}.exposure`] = 'Exposure is not editable for this class.';
    }
    if (!/^\d+\.\d{1,3}$/.test(row.manualRate)) {
      errors[`${prefix}.manualRate`] = 'Manual rate is required and must include a decimal with up to three decimal places (e.g., 1.125).';
    }
    if (!/^\d+$/.test(row.premium) || row.premium === '') {
      errors[`${prefix}.premium`] = 'Premium must be a whole dollar amount.';
    }
    for (const [field, value] of [
      ['expModFactorEffectiveDate', row.expModFactorEffectiveDate],
      ['rateEffectiveDate', row.rateEffectiveDate],
    ] as const) {
      if (!value) {
        errors[`${prefix}.${field}`] = 'Date is required.';
      } else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        errors[`${prefix}.${field}`] = 'Date must use MM/DD/YYYY format.';
      }
    }
  });
  return errors;
};

// Builds one summary line per split touched by new exposure rows: an existing split gets a
// "New Exposure added to <Period>" line per row, while a brand-new split gets a single
// "<Period> Split Added with N exposure(s)" line.
export const getUnitStatExposureChangedFieldSummaries = (
  snapshot: UnitStatExposureDraftDelta,
  draft: UnitStatExposureDraftDelta,
  splitPeriodTypes: CodeDescriptionLookupOption[] = []
): string[] => {
  const summaries: string[] = [];
  const deletedIdSet = new Set(draft.deletedExposureIds);
  // A split code only counts as "still existing" if at least one of its original exposures
  // survived the edit; if every original exposure for that code was deleted, a new row reusing
  // the code is really recreating the split rather than adding to it.
  const existingSplitCodes = new Set(
    snapshot.rows
      .filter((row) => row.usrexposureId === undefined || !deletedIdSet.has(row.usrexposureId))
      .map((row) => row.splitPeriodCode)
  );
  const newRowsBySplitCode = new Map<string, UnitStatExposureDraftRow[]>();
  draft.rows
    .filter((row) => row.isNew)
    .forEach((row) => {
      if (!newRowsBySplitCode.has(row.splitPeriodCode)) newRowsBySplitCode.set(row.splitPeriodCode, []);
      newRowsBySplitCode.get(row.splitPeriodCode)!.push(row);
    });

  const getSplitDescription = (code: string) =>
    splitPeriodTypes.find((option) => option.code === code)?.description ?? `Split ${code}`;

  newRowsBySplitCode.forEach((rows, splitCode) => {
    const description = getSplitDescription(splitCode);
    if (existingSplitCodes.has(splitCode)) {
      rows.forEach(() => summaries.push(`New Exposure added to ${description}`));
    } else {
      summaries.push(`${description} Split Added with ${rows.length} exposure${rows.length === 1 ? '' : 's'}`);
    }
  });

  const snapshotExposureIds = new Set(
    snapshot.rows.map((row) => row.usrexposureId).filter((id): id is number => id !== undefined)
  );
  draft.deletedExposureIds
    .filter((id) => snapshotExposureIds.has(id))
    .forEach((id) => {
      const deletedRow = snapshot.rows.find((row) => row.usrexposureId === id)!;
      const classDescription = deletedRow.classDescription ? ` - ${deletedRow.classDescription}` : '';
      const splitDescription = getSplitDescription(deletedRow.splitPeriodCode);
      summaries.push(`Exposure (${deletedRow.classCode}${classDescription}) of ${splitDescription} Split Deleted`);
    });

  return summaries;
};
